import React, { useEffect, useState } from 'react'
import Typography from '@mui/material/Typography'
import { Alert, Checkbox, FormControlLabel, TextField } from '@mui/material'
import Dialog from '../../../../../dialog/Dialog'
import ButtonStartIcon from '../../../../../button/ButtonStartIcon'

// MF-594 — ACER OARS sends a 6-digit code to the email of the account profile at each login from a device it does
// not know. The sync waits (a few minutes) and the person types the code here; Maestro sends it to ACER and the sync
// continues. "Remember this device" lets ACER skip the code on the next syncs (30 days).
const secondsLeft = (until) =>
  until ? Math.max(0, Math.floor((new Date(until).getTime() - Date.now()) / 1000)) : 0
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

const OtpDialog = ({ show, credential = {}, onClose, onSubmit }) => {
  const [code, setCode] = useState('')
  const [remember, setRemember] = useState(true)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [left, setLeft] = useState(() => secondsLeft(credential.awaitingCodeUntil))

  useEffect(() => {
    setLeft(secondsLeft(credential.awaitingCodeUntil))
    const timer = setInterval(() => setLeft(secondsLeft(credential.awaitingCodeUntil)), 1000)
    return () => clearInterval(timer)
  }, [credential.awaitingCodeUntil])

  const expired = left <= 0
  const ready = /^\d{6}$/.test(code) && !expired

  const submit = async () => {
    if (!ready || loading) return
    setLoading(true)
    setError('')
    try {
      const result = await onSubmit(code, remember)
      if (result?.error) {
        setError(result.message || 'The code could not be verified')
        setCode('')
      }
    } finally {
      setLoading(false)
    }
  }

  const Actions = (
    <>
      <ButtonStartIcon onClick={onClose} color='primary' variant='outlined' startIcon={['fal', 'times']}>
        Cancel
      </ButtonStartIcon>
      <ButtonStartIcon
        onClick={submit}
        color='primary'
        variant='contained'
        loading={loading}
        disabled={!ready}
        startIcon={['fas', 'check']}
      >
        Verify and continue
      </ButtonStartIcon>
    </>
  )

  return (
    <Dialog
      id='third-party-access-otp'
      show={show}
      title={`Verification code — ${credential.label || credential.schoolSlug || ''}`}
      maxWidth='xs'
      fullWidth
      onClose={onClose}
      actions={Actions}
    >
      <Typography variant='body2' sx={{ mb: 2 }}>
        ACER OARS sent a 6-digit code to the email address of the account profile ({credential.username}).
        Type it here to continue the sync.
      </Typography>

      {expired ? (
        <Alert severity='warning' sx={{ mb: 2 }}>
          The code expired. Close this window and start the sync again to get a new one.
        </Alert>
      ) : (
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
          The code expires in <b>{mmss(left)}</b>.
        </Typography>
      )}

      <TextField
        autoFocus
        fullWidth
        label='Code'
        value={code}
        disabled={expired || loading}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        onKeyDown={(e) => e.key === 'Enter' && submit()}
        inputProps={{
          inputMode: 'numeric',
          autoComplete: 'one-time-code',
          maxLength: 6,
          style: { letterSpacing: 6 }
        }}
        error={Boolean(error)}
        helperText={error || ' '}
      />

      <FormControlLabel
        control={
          <Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)} disabled={loading} />
        }
        label='Remember this connection for 30 days'
      />
      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', ml: 4 }}>
        The next syncs will not ask for a code until ACER stops trusting it.
      </Typography>
    </Dialog>
  )
}

export default OtpDialog
